import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Gavel, CheckCircle, XCircle, Shield, MessageSquare, Scale } from 'lucide-react';

const RuleItem = ({ icon, children, delay }) => (
    <motion.li 
        className="flex items-start gap-4"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: delay, duration: 0.5 }}
    >
        <div className="flex-shrink-0 mt-1 text-cyan-400">{icon}</div>
        <span className="text-slate-300">{children}</span>
    </motion.li>
);

const Rules = () => (
    <>
    <Helmet>
        <title>Platform Rules & Guidelines | RatingApp</title>
        <meta name="description" content="Read RatingApp's platform rules and guidelines. Learn about authentic ratings, fair play, account security, and prohibited activities to stay compliant." />
    </Helmet>
    <div className="w-full min-h-screen  from-gray-900 via-blue-900/30 to-gray-900 text-white font-sans p-4">
        <div className="max-w-2xl mx-auto py-12">
            
            <motion.div 
                className="text-center mb-16"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <Gavel size={48} className="mx-auto text-cyan-400 mb-4" />
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 mb-3">
                    Platform Rules & Guidelines
                </h1>
                <p className="text-slate-400 max-w-2xl mx-auto text-lg">
                    To ensure a fair, secure, and rewarding experience for everyone, please adhere to the following rules.
                </p>
            </motion.div>

            <div className="bg-gray-800/20 backdrop-blur-lg border border-white/10 rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-6 text-slate-100">Core Principles</h2>
                <ul className="space-y-5">
                    <RuleItem icon={<CheckCircle size={20} />} delay={0.2}>
                        <strong>Authentic Ratings:</strong> Only rate and review applications you have genuinely used. Your honest feedback is the cornerstone of our platform's value.
                    </RuleItem>
                    <RuleItem icon={<Scale size={20} />} delay={0.3}>
                        <strong>Fair Play:</strong> Do not attempt to manipulate ratings, use automated scripts, or create multiple accounts to gain an unfair advantage.
                    </RuleItem>
                    <RuleItem icon={<MessageSquare size={20} />} delay={0.4}>
                        <strong>Respectful Conduct:</strong> Maintain a respectful and constructive tone in all interactions. Offensive language or harassment will not be tolerated.
                    </RuleItem>
                    <RuleItem icon={<Shield size={20} />} delay={0.5}>
                        <strong>Account Security:</strong> You are responsible for the security of your account. Do not share your password or security PIN with anyone.
                    </RuleItem>
                     <RuleItem icon={<XCircle size={20} />} delay={0.6}>
                        <strong>Prohibited Activities:</strong> Any form of money laundering, fraudulent activity, or exploitation of the system is strictly forbidden and will result in immediate account termination.
                    </RuleItem>
                </ul>
            </div>

             <motion.div 
                className="mt-12 text-center bg-gray-800/30 border border-blue-500/20 p-6 rounded-2xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
             >
                <h3 className="font-bold text-lg text-slate-100">Violation of these rules may lead to temporary suspension or permanent termination of your account.</h3>
                <p className="text-slate-400 text-sm mt-2">
                    If you have questions or need to report a violation, please contact our support team at <a href="mailto:support@ratingapp.com" className="text-cyan-400 hover:underline">support@ratingapp.com</a>.
                </p>
            </motion.div>

        </div>
    </div>
    </>
);

export default Rules; 